import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { completeDeposit } from '../../store/slices/depositsSlice';
import { Deposit, BankType } from '../../types';
import apiService from '../../services/api';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
  bankTransaction: yup
    .string()
    .required('Transaction ID, receipt link, or bank SMS is required')
    .min(3, 'Proof looks too short'),
  bankType: yup.string().required('Bank type is required'),
});

type FormData = {
  bankTransaction: string;
  bankType: BankType | string;
};

interface CompleteDepositModalProps {
  open: boolean;
  onClose: () => void;
  deposit: Deposit | null;
  onSuccess: () => void;
}

const CompleteDepositModal: React.FC<CompleteDepositModalProps> = ({
  open,
  onClose,
  deposit,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);

  const isCrypto = Boolean(
    deposit?.paymentMethodType === 'crypto' ||
    deposit?.cryptoAmount ||
    deposit?.bankType === BankType.CRYPTO ||
    deposit?.bankType === BankType.BINANCE_PAY ||
    deposit?.bankType === ('crypto' as any)
  );

  const { control, handleSubmit, reset, getValues, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      bankTransaction: '',
      bankType: isCrypto ? BankType.CRYPTO : (deposit?.bankType || BankType.TELEBIRR),
    },
  });

  const [autoVerifying, setAutoVerifying] = React.useState(false);

  React.useEffect(() => {
    if (open && deposit) {
      const cryptoActive = Boolean(
        deposit.paymentMethodType === 'crypto' ||
        deposit.cryptoAmount ||
        deposit.bankType === BankType.CRYPTO ||
        deposit.bankType === BankType.BINANCE_PAY ||
        deposit.bankType === ('crypto' as any)
      );
      reset({
        bankTransaction: deposit.cryptoTxId || deposit.bankTransaction || deposit.smsText || '',
        bankType: cryptoActive ? BankType.CRYPTO : (deposit.bankType || BankType.TELEBIRR),
      });
    }
  }, [open, deposit, reset]);

  const handleAutoVerifyBinance = async () => {
    if (!deposit) return;
    setAutoVerifying(true);
    try {
      const txId = getValues('bankTransaction')?.trim() || deposit.cryptoTxId;
      const res = await apiService.verifyCryptoDeposit(deposit.reference || deposit.id, {
        cryptoTxId: txId,
      });
      if (res.success) {
        toast.success(res.message || 'Binance Pay payment verified and wallet credited!');
        onSuccess();
        onClose();
      } else {
        toast.error(res.message || 'Binance Pay verification did not find matching payment');
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || err?.message || 'Binance Pay auto-verify failed';
      toast.error(errMsg);
    } finally {
      setAutoVerifying(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!deposit) return;

    setLoading(true);
    try {
      const result = await dispatch(completeDeposit({
        depositId: deposit.reference || deposit.id,
        bankTransaction: data.bankTransaction.trim(),
        bankType: isCrypto ? BankType.CRYPTO : String(data.bankType),
      }) as any);

      if (completeDeposit.fulfilled.match(result) && result.payload?.success) {
        toast.success(result.payload.message || (isCrypto ? 'Crypto deposit credited successfully' : 'Verified with JustVerify and completed'));
        onSuccess();
        onClose();
      } else {
        const msg =
          result.payload ||
          result.error?.message ||
          (isCrypto ? 'Failed to complete crypto deposit' : 'JustVerify failed — deposit was not completed');
        toast.error(typeof msg === 'string' ? msg : 'Deposit could not be completed');
      }
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred while verifying deposit');
    } finally {
      setLoading(false);
    }
  };

  const bankTypes = [
    { value: BankType.CRYPTO, label: 'Binance Pay / Crypto' },
    { value: BankType.TELEBIRR, label: 'Telebirr' },
    { value: BankType.CBE, label: 'CBE' },
    { value: BankType.CBEBIRR, label: 'CBE Birr' },
    { value: BankType.ABYSSINIA, label: 'Abyssinia (BoA)' },
    { value: 'BoA', label: 'BoA' },
    { value: BankType.AMOLE, label: 'Amole' },
    { value: BankType.AWASH, label: 'Awash' },
    { value: BankType.OTHER, label: 'Other' },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Typography variant="h6">
          {isCrypto ? '🪙 Verify & Credit Binance Deposit' : 'Verify & Complete Deposit'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {deposit && (
          <Box sx={{ mt: 2 }}>
            {isCrypto ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                🪙 <strong>Crypto Deposit (Binance Pay)</strong> — This deposit bypasses JustVerify.
                Completing will directly credit the player&apos;s wallet with{' '}
                <strong>{formatCurrency(deposit.amount)}</strong>.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                JustVerify is required. The deposit will <strong>not</strong> be credited unless
                the transaction is confirmed (amount + our account).
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {deposit.user?.username || 'Unknown User'} — {formatCurrency(deposit.amount)}
              </Typography>
              {isCrypto && deposit.cryptoAmount && (
                <Typography variant="caption" display="block">
                  Amount: <strong>{deposit.cryptoAmount} {deposit.cryptoCurrency || 'USDT'}</strong> (Rate: 1 USDT = {deposit.exchangeRate || 135} ETB)
                </Typography>
              )}
              {deposit.reference && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Ref: {deposit.reference}
                </Typography>
              )}
            </Alert>

            <Controller
              name="bankTransaction"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={isCrypto ? 2 : 3}
                  label={isCrypto ? 'Binance Transaction ID / Order ID' : 'Transaction proof'}
                  placeholder={
                    isCrypto
                      ? 'Paste Binance Pay Order ID or Transaction ID'
                      : 'Paste FT number, Telebirr txn ID, receipt URL, or full bank SMS'
                  }
                  error={!!errors.bankTransaction}
                  helperText={
                    errors.bankTransaction?.message ||
                    (isCrypto
                      ? 'Enter the Binance Pay transaction or order ID'
                      : 'JustVerify will check this before approving')
                  }
                  sx={{ mb: 2 }}
                />
              )}
            />

            {isCrypto && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={autoVerifying || loading}
                  onClick={handleAutoVerifyBinance}
                  startIcon={autoVerifying ? <CircularProgress size={16} /> : undefined}
                >
                  {autoVerifying ? 'Checking Binance API...' : 'Auto-Verify with Binance'}
                </Button>
              </Box>
            )}

            <Controller
              name="bankType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.bankType}>
                  <InputLabel>Payment Method / Bank</InputLabel>
                  <Select {...field} label="Payment Method / Bank">
                    {deposit.bankType &&
                      !bankTypes.some((b) => b.value === deposit.bankType) && (
                        <MenuItem value={deposit.bankType}>{deposit.bankType}</MenuItem>
                      )}
                    {bankTypes.map((bank) => (
                      <MenuItem key={bank.value} value={bank.value}>
                        {bank.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.bankType && (
                    <Typography variant="caption" color="error">
                      {errors.bankType.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? 'Verifying…' : 'Verify & Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteDepositModal;
